import { Fragment, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superadminApi, type UserSearchResultDto, type SupportRequestDto } from '@/api/superadmin'
import { Search, Copy, Check, UserX, Shield, ClipboardList, ChevronLeft, ChevronRight, LifeBuoy, ChevronDown, Mail, RotateCcw, UserCog, Link2, KeyRound, Trash2 } from 'lucide-react'
import { ActionsMenu, type ActionItem } from '@/components/shared/ActionsMenu'
import { ChangeEmailDialog, ChangeRoleDialog, ConfirmDialog, ROLE_LABEL } from './AccountDialogs'

type Tab = 'requests' | 'users' | 'audit'

function fmt(date: string) {
  return new Date(date).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ── User lookup ────────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  Active: { label: 'Active', cls: 'bg-green-50 text-green-700 border-green-200' },
  InviteNotAccepted: { label: 'Invite not accepted', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  Deactivated: { label: 'Deactivated', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
}

type Dialog =
  | { kind: 'email'; user: UserSearchResultDto }
  | { kind: 'role'; user: UserSearchResultDto }
  | { kind: 'reactivate'; user: UserSearchResultDto }
  | { kind: 'delete'; user: UserSearchResultDto }

function UserLookup() {
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [copied, setCopied] = useState(false)
  const [link, setLink] = useState<{ userId: string; kind: 'reset' | 'invite'; link: string } | null>(null)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)

  const { data: users, isFetching, error } = useQuery({
    queryKey: ['superadmin-user-search', search],
    queryFn: () => superadminApi.searchUsers(search).then(r => r.data),
    enabled: search.length >= 3,
  })

  const linkMutation = useMutation({
    mutationFn: ({ userId, kind }: { userId: string; kind: 'reset' | 'invite' }) =>
      (kind === 'reset' ? superadminApi.getResetLink(userId) : superadminApi.getInviteLink(userId)).then(r => r.data.link),
    onSuccess: (l, v) => { setLinkError(null); setLink({ userId: v.userId, kind: v.kind, link: l }) },
    onError: e => setLinkError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not create the link.'),
  })

  const refresh = () => {
    setLink(null)
    queryClient.invalidateQueries({ queryKey: ['superadmin-user-search'] })
    queryClient.invalidateQueries({ queryKey: ['superadmin-audit-log'] })
  }

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLink(null)
    setSearch(query.trim())
  }

  const menuFor = (u: UserSearchResultDto): ActionItem[] => {
    const a = u.actions
    const hint = (x: { allowed: boolean; reason: string | null }) => (x.allowed ? undefined : x.reason ?? undefined)
    return [
      { label: 'Change email', icon: <Mail size={14} />, onClick: () => setDialog({ kind: 'email', user: u }), disabled: !a.changeEmail.allowed, hint: hint(a.changeEmail) },
      { label: 'Reactivate', icon: <RotateCcw size={14} />, onClick: () => setDialog({ kind: 'reactivate', user: u }), disabled: !a.reactivate.allowed, hint: hint(a.reactivate) },
      { label: 'Change role', icon: <UserCog size={14} />, onClick: () => setDialog({ kind: 'role', user: u }), disabled: !a.changeRole.allowed, hint: hint(a.changeRole) },
      { label: 'Get invite link', icon: <Link2 size={14} />, onClick: () => linkMutation.mutate({ userId: u.id, kind: 'invite' }), disabled: !a.inviteLink.allowed, hint: hint(a.inviteLink) },
      { label: 'Get password reset link', icon: <KeyRound size={14} />, onClick: () => linkMutation.mutate({ userId: u.id, kind: 'reset' }) },
      { label: 'Delete account', icon: <Trash2 size={14} />, onClick: () => setDialog({ kind: 'delete', user: u }), disabled: !a.delete.allowed, hint: hint(a.delete), danger: true },
    ]
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by email…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <button
          type="submit"
          disabled={query.trim().length < 3}
          className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 cursor-pointer"
        >
          Search
        </button>
      </form>

      <p className="text-xs text-gray-500 max-w-2xl">
        An email can only belong to one login across every organisation. If adding an account says it already exists, find it here:
        you can change its email, reactivate it, remove it, change its role, or send a link. Everything you do here is recorded in the audit log.
      </p>

      {isFetching && <p className="text-sm text-gray-400">Searching…</p>}
      {error && <p className="text-sm text-red-500">Search failed. Try again.</p>}
      {linkError && <p className="text-sm text-red-600">{linkError}</p>}

      {users && users.length === 0 && (
        <p className="text-sm text-gray-400">No users found for "{search}".</p>
      )}

      {users && users.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Account</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Organisation</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u: UserSearchResultDto) => {
                const badge = STATUS_BADGE[u.status] ?? STATUS_BADGE.Deactivated
                return (
                  <Fragment key={u.id}>
                    <tr className="hover:bg-gray-50/60 align-top">
                      <td className="px-5 py-3">
                        <p className="font-medium text-gray-900 break-all">{u.email}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {[u.linkedTo, u.clientName && `Client: ${u.clientName}`, `Created ${new Date(u.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`].filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="px-5 py-3 text-gray-500">
                        {u.tenantName ?? <span className="text-gray-300 italic">unknown</span>}
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 whitespace-nowrap">
                          <Shield size={11} className="text-gray-400" />
                          {ROLE_LABEL[u.role] ?? u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${badge.cls}`}>
                          {u.status !== 'Active' && <UserX size={11} />} {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right"><ActionsMenu items={menuFor(u)} /></td>
                    </tr>
                    {link?.userId === u.id && (
                      <tr className="bg-indigo-50/40">
                        <td colSpan={5} className="px-5 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-medium text-gray-600">{link.kind === 'invite' ? 'Invite link' : 'Password reset link'}</span>
                            <input readOnly value={link.link} className="text-xs border border-gray-200 rounded px-2 py-1 flex-1 min-w-48 font-mono text-gray-600 bg-white" />
                            <button
                              onClick={() => handleCopy(link.link)}
                              className="text-xs font-medium px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer flex items-center gap-1"
                            >
                              {copied ? <Check size={11} className="text-green-600" /> : <Copy size={11} />}
                              {copied ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.kind === 'email' && <ChangeEmailDialog account={dialog.user} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.kind === 'role' && <ChangeRoleDialog account={dialog.user} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.kind === 'reactivate' && (
        <ConfirmDialog
          title="Reactivate this account?"
          confirmLabel="Reactivate"
          run={() => superadminApi.reactivateUser(dialog.user.id)}
          onClose={() => setDialog(null)}
          onDone={refresh}
        >
          <p><span className="font-medium text-gray-900">{dialog.user.email}</span> will be able to sign in again with their existing password.</p>
        </ConfirmDialog>
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          title="Delete this account?"
          confirmLabel="Delete account"
          danger
          run={() => superadminApi.deleteUser(dialog.user.id)}
          onClose={() => setDialog(null)}
          onDone={refresh}
        >
          <p><span className="font-medium text-gray-900">{dialog.user.email}</span> will be signed out and can no longer sign in.</p>
          <ul className="list-disc pl-4 text-xs text-gray-500 space-y-0.5">
            <li>The email address is released, so it can be used for a new account.</li>
            <li>Their history is kept; the account just becomes unusable.</li>
            {dialog.user.linkedTo?.startsWith('Trainer') && <li>The trainer record stays, without a login.</li>}
            <li>This can't be undone from here.</li>
          </ul>
        </ConfirmDialog>
      )}
    </div>
  )
}

// ── Support requests ─────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'Open', label: 'Open' },
  { id: 'InProgress', label: 'In progress' },
  { id: 'Resolved', label: 'Resolved' },
]

const CATEGORY_LABEL: Record<string, string> = {
  Question: 'Question',
  Problem: 'Problem',
  Billing: 'Billing',
  FeatureRequest: 'Feature request',
  Other: 'Other',
  CallRequest: 'Call / demo request',
}

function statusBadgeClass(status: string) {
  switch (status) {
    case 'Open': return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'InProgress': return 'bg-blue-50 text-blue-700 border-blue-200'
    case 'Resolved': return 'bg-green-50 text-green-700 border-green-200'
    default: return 'bg-gray-100 text-gray-500 border-gray-200'
  }
}

function statusLabel(status: string) {
  return status === 'InProgress' ? 'In progress' : status
}

function RequestRow({ request }: { request: SupportRequestDto }) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [status, setStatus] = useState(request.status)
  const [notes, setNotes] = useState(request.internalNotes ?? '')

  const updateMutation = useMutation({
    mutationFn: () => superadminApi.updateSupportRequest(request.id, { status, internalNotes: notes || null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superadmin-support-requests'] }),
  })

  const dirty = status !== request.status || notes !== (request.internalNotes ?? '')

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-gray-50/60 cursor-pointer"
      >
        <ChevronDown size={14} className={`text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${statusBadgeClass(request.status)}`}>
          {statusLabel(request.status)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">{request.subject}</span>
            <span className="text-xs text-gray-400 shrink-0">{CATEGORY_LABEL[request.category] ?? request.category}</span>
          </div>
          <p className="text-xs text-gray-500 truncate">{request.tenantName} · {request.submittedByEmail}</p>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">{fmt(request.createdAt)}</span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Message</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5">
              {request.message}
            </p>
          </div>
          {request.phone && (
            <p className="text-sm text-gray-700">Call them on: <a href={`tel:${request.phone}`} className="font-medium text-indigo-600 hover:underline">{request.phone}</a></p>
          )}
          {request.pageUrl && (
            <p className="text-xs text-gray-400">Raised from: <span className="font-mono">{request.pageUrl}</span></p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Open">Open</option>
                <option value="InProgress">In progress</option>
                <option value="Resolved">Resolved</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
              Internal notes <span className="normal-case text-gray-400">(not visible to the customer)</span>
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Triage notes, links to related tickets, resolution summary…"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => updateMutation.mutate()}
              disabled={!dirty || updateMutation.isPending}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 cursor-pointer transition-colors"
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {updateMutation.isSuccess && !dirty && (
              <span className="text-sm text-green-600 flex items-center gap-1"><Check size={13} /> Saved</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function RequestsQueue() {
  const [statusFilter, setStatusFilter] = useState('')

  const { data: requests, isLoading } = useQuery({
    queryKey: ['superadmin-support-requests', statusFilter],
    queryFn: () => superadminApi.getSupportRequests(statusFilter || undefined).then(r => r.data),
  })

  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
              statusFilter === f.id
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>}
      {!isLoading && requests?.length === 0 && (
        <p className="text-sm text-gray-400 py-8 text-center">No support requests{statusFilter ? ` with status "${statusLabel(statusFilter)}"` : ''}.</p>
      )}

      <div className="space-y-2">
        {requests?.map(r => <RequestRow key={r.id} request={r} />)}
      </div>
    </div>
  )
}

// ── Audit log ──────────────────────────────────────────────────────────────────

function AuditLog() {
  const [page, setPage] = useState(1)
  const [tenantId, setTenantId] = useState('')
  const pageSize = 50

  const { data: tenants } = useQuery({
    queryKey: ['superadmin-tenants'],
    queryFn: () => superadminApi.getTenants().then(r => r.data),
  })
  const tenantName = (id: string | null) => tenants?.find(t => t.id === id)?.name

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-audit-log', page, tenantId],
    queryFn: () => superadminApi.getAuditLog({ tenantId: tenantId || undefined, page, pageSize }).then(r => r.data),
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 1

  return (
    <div className="space-y-4">
      <select
        value={tenantId}
        onChange={e => { setTenantId(e.target.value); setPage(1) }}
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
        aria-label="Filter by tenant"
      >
        <option value="">All tenants</option>
        {tenants?.slice().sort((a, b) => a.name.localeCompare(b.name)).map(t => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">When</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tenant</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actor</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-12">Loading…</td></tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-12">No audit entries yet.</td></tr>
            )}
            {data?.items.map(item => (
              <tr key={item.id} className="hover:bg-gray-50/60">
                <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{fmt(item.createdAt)}</td>
                <td className="px-5 py-3 text-gray-700">{tenantName(item.tenantId) ?? '—'}</td>
                <td className="px-5 py-3 text-gray-700 font-mono text-xs">{item.actorEmail ?? '—'}</td>
                <td className="px-5 py-3 font-medium text-gray-900">{item.action}</td>
                <td className="px-5 py-3 text-gray-500 text-xs">{item.detail ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{data?.total ?? 0} entries</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              <ChevronLeft size={14} />
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function SupportPage() {
  const [tab, setTab] = useState<Tab>('requests')

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'requests', label: 'Requests', icon: <LifeBuoy size={14} /> },
    { id: 'users', label: 'User lookup', icon: <Search size={14} /> },
    { id: 'audit', label: 'Audit log', icon: <ClipboardList size={14} /> },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Support</h1>
        <p className="text-sm text-gray-500 mt-1">Customer requests, user lookup, password reset links, and system audit log</p>
      </div>

      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
              tab === t.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'requests' && <RequestsQueue />}
      {tab === 'users' && <UserLookup />}
      {tab === 'audit' && <AuditLog />}
    </div>
  )
}
